'use client';

import React, { useState, useMemo } from 'react';
import {
    Receipt,
    Search,
    Filter,
    ArrowLeft,
    ArrowRight,
    CreditCard,
    CheckCircle2,
    Clock,
    Wallet,
    Download,
    Eye,
    ChevronDown,
    AlertCircle,
    History,
    FileText,
    Banknote,
    QrCode,
    Smartphone
} from 'lucide-react';

type InvoiceStatus = 'Lunas' | 'Belum Bayar' | 'Menunggak' | 'Pending';
type ViewMode = 'list' | 'detail' | 'payment' | 'success';

interface Invoice {
    id: string;
    title: string;
    type: string;
    amount: number;
    dueDate: string;
    status: InvoiceStatus;
    items: { name: string; price: number }[];
    fine?: number;
}

export default function TagihanPage() {
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [paymentMethod, setPaymentMethod] = useState<string | null>(null);

    // --- Mock Data ---
    const dummyInvoices: Invoice[] = [
        {
            id: 'INV-2026-001',
            title: 'SPP Februari 2026',
            type: 'SPP Bulanan',
            amount: 750000,
            dueDate: '10 Feb 2026',
            status: 'Belum Bayar',
            items: [
                { name: 'SPP Bulanan - Februari', price: 750000 }
            ]
        },
        {
            id: 'INV-2026-002',
            title: 'SPP Januari 2026',
            type: 'SPP Bulanan',
            amount: 750000,
            dueDate: '10 Jan 2026',
            status: 'Lunas',
            items: [
                { name: 'SPP Bulanan - Januari', price: 750000 }
            ]
        },
        {
            id: 'INV-2025-088',
            title: 'Uang Buku Semester Genap',
            type: 'Iuran Opsional',
            amount: 1250000,
            dueDate: '20 Jan 2026',
            status: 'Lunas',
            items: [
                { name: 'Paket Buku Tematik', price: 800000 },
                { name: 'Buku Penunjang Tahfidz', price: 450000 }
            ]
        },
        {
            id: 'INV-2025-045',
            title: 'Iuran Ekstrakurikuler Robotik',
            type: 'Ekskul',
            amount: 350000,
            dueDate: '05 Jan 2026',
            status: 'Menunggak',
            fine: 25000,
            items: [
                { name: 'Biaya Pelatih & Kit Robot', price: 350000 }
            ]
        }
    ];

    const filteredInvoices = useMemo(() => {
        return dummyInvoices.filter(inv =>
            inv.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inv.id.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm]);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    // --- Handlers ---
    const handleViewDetail = (inv: Invoice) => {
        setSelectedInvoice(inv);
        setViewMode('detail');
    };

    const handleBayar = () => {
        setViewMode('payment');
    };

    const handleConfirmPayment = () => {
        setViewMode('success');
    };

    const handleBack = () => {
        if (viewMode === 'detail') setViewMode('list');
        if (viewMode === 'payment') setViewMode('detail');
        if (viewMode === 'success') {
            setViewMode('list');
            setSelectedInvoice(null);
            setPaymentMethod(null);
        }
    };

    // --- Sub-Views ---

    const ListView = () => (
        <div className="space-y-8 animate-in fade-in duration-700">
            {/* Header List */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-black text-foreground tracking-tight flex items-center gap-4">
                        <Receipt className="w-8 h-8 text-primary" /> Tagihan Pendidikan
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 font-medium">Kelola dan selesaikan seluruh biaya pendidikan anak Anda.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                        <input
                            type="text"
                            placeholder="Cari tagihan..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-11 pr-5 py-3.5 bg-surface border border-border-main rounded-2xl text-sm focus:ring-2 focus:ring-primary/20 outline-none w-full md:w-64"
                        />
                    </div>
                </div>
            </div>

            {/* Invoices Table */}
            <div className="bg-surface border border-border-main rounded-[32px] overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-8 py-5 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">ID & Deskripsi Tagihan</th>
                                <th className="px-8 py-5 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">Kategori</th>
                                <th className="px-8 py-5 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">Jatuh Tempo</th>
                                <th className="px-8 py-5 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] text-center">Status</th>
                                <th className="px-8 py-5 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] text-right">Total Tagihan</th>
                                <th className="px-8 py-5 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main/50">
                            {filteredInvoices.map((inv) => (
                                <tr
                                    key={inv.id}
                                    className="hover:bg-primary-soft/30 transition-colors group cursor-pointer"
                                    onClick={() => handleViewDetail(inv)}
                                >
                                    <td className="px-8 py-6">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center text-primary shadow-xs group-hover:scale-110 transition-transform">
                                                <Receipt className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-black text-foreground group-hover:text-primary transition-colors">{inv.title}</p>
                                                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest leading-none mt-1">{inv.id}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6 whitespace-nowrap">
                                        <span className="text-[11px] font-bold text-secondary uppercase bg-background px-3 py-1 rounded-lg border border-border-main/50">{inv.type}</span>
                                    </td>
                                    <td className="px-8 py-6 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4 text-muted" />
                                            <span className="text-xs font-bold text-foreground">{inv.dueDate}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6 text-center whitespace-nowrap">
                                        <span className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-wider border ${inv.status === 'Lunas' ? 'bg-success-soft text-success border-success/10' :
                                            inv.status === 'Menunggak' ? 'bg-error-soft text-error border-error/10' :
                                                'bg-warning-soft text-warning border-warning/10'
                                            }`}>
                                            {inv.status}
                                        </span>
                                    </td>
                                    <td className="px-8 py-6 text-right whitespace-nowrap">
                                        <p className="text-sm font-black text-foreground tabular-nums">{formatCurrency(inv.amount)}</p>
                                    </td>
                                    <td className="px-8 py-6 text-right whitespace-nowrap">
                                        <button className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center text-muted group-hover:text-primary group-hover:border-primary transition-all shadow-xs">
                                            <Eye className="w-5 h-5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* Table Footer */}
                <div className="px-8 py-6 bg-background/30 border-t border-border-main flex justify-between items-center">
                    <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Menampilkan {filteredInvoices.length} Tagihan Terdaftar</p>
                    <div className="flex gap-2">
                        <button className="px-4 py-2 border border-border-main rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-white transition-all disabled:opacity-30" disabled>Previous</button>
                        <button className="px-4 py-2 border border-border-main rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-white transition-all disabled:opacity-30" disabled>Next Page</button>
                    </div>
                </div>
            </div>
        </div>
    );

    const DetailView = () => {
        if (!selectedInvoice) return null;
        return (
            <div className="max-w-7xl mx-auto space-y-8 animate-in slide-in-from-right duration-500">
                <button onClick={handleBack} className="flex items-center gap-2 text-muted-foreground hover:text-primary font-bold text-xs uppercase tracking-widest transition-colors mb-4">
                    <ArrowLeft className="w-4 h-4" /> Kembali ke Daftar
                </button>

                <div className="bg-surface border border-border-main rounded-[40px] shadow-sm overflow-hidden">
                    <div className="p-10 border-b border-border-main bg-background/20 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-6 text-center md:text-left">
                            <div className="w-16 h-16 rounded-2xl bg-primary-soft flex items-center justify-center text-primary">
                                <Receipt className="w-8 h-8" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-black text-foreground tracking-tight">{selectedInvoice.title}</h2>
                                <p className="text-sm font-medium text-muted-foreground">{selectedInvoice.id} • {formatCurrency(selectedInvoice.amount)}</p>
                            </div>
                        </div>
                        <div className={`px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest border ${selectedInvoice.status === 'Lunas' ? 'bg-success text-white' : 'bg-error text-white'
                            }`}>
                            {selectedInvoice.status}
                        </div>
                    </div>

                    <div className="p-10 space-y-10">
                        {/* Summary Items */}
                        <div className="space-y-4">
                            <h4 className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-6 border-b pb-4">Rincian Pembayaran</h4>
                            {selectedInvoice.items.map((item, idx) => (
                                <div key={idx} className="flex justify-between items-center py-2">
                                    <span className="text-sm font-bold text-foreground">{item.name}</span>
                                    <span className="text-sm font-black tabular-nums">{formatCurrency(item.price)}</span>
                                </div>
                            ))}
                            {selectedInvoice.fine && (
                                <div className="flex justify-between items-center py-2 text-error">
                                    <span className="text-sm font-bold">Denda Keterlambatan</span>
                                    <span className="text-sm font-black tabular-nums">+{formatCurrency(selectedInvoice.fine)}</span>
                                </div>
                            )}
                            <div className="pt-6 mt-6 border-t-2 border-border-main border-dashed flex justify-between items-center">
                                <span className="text-lg font-black text-foreground uppercase tracking-tight">Total Akhir</span>
                                <span className="text-2xl font-black text-primary tabular-nums">{formatCurrency(selectedInvoice.amount + (selectedInvoice.fine || 0))}</span>
                            </div>
                        </div>

                        {/* Additional Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="p-6 bg-background rounded-3xl border border-border-main border-dashed">
                                <p className="text-[9px] font-black text-muted-foreground uppercase tracking-[0.2em] mb-4">Informasi Tagihan</p>
                                <div className="space-y-3">
                                    <div className="flex justify-between text-xs">
                                        <span className="text-muted-foreground font-medium">Batas Waktu:</span>
                                        <span className="text-foreground font-black">{selectedInvoice.dueDate}</span>
                                    </div>
                                    <div className="flex justify-between text-xs">
                                        <span className="text-muted-foreground font-medium">Tipe:</span>
                                        <span className="text-foreground font-black">{selectedInvoice.type}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col justify-center">
                                {selectedInvoice.status !== 'Lunas' ? (
                                    <button
                                        onClick={handleBayar}
                                        className="w-full bg-primary text-white font-black py-4 rounded-2xl shadow-xl shadow-primary/20 hover:scale-[1.02] transition-transform flex items-center justify-center gap-3"
                                    >
                                        Bayar Sekarang <ArrowRight className="w-5 h-5" />
                                    </button>
                                ) : (
                                    <button className="w-full bg-success/10 text-success border border-success/20 font-black py-4 rounded-2xl flex items-center justify-center gap-3">
                                        <CheckCircle2 className="w-5 h-5" /> Unduh Kwitansi (PDF)
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const PaymentView = () => {
        const paymentMethods = [
            { id: 'va', name: 'Virtual Account', icon: CreditCard, provider: 'BNI, Mandiri, BRI, Permata' },
            { id: 'qris', name: 'QRIS / E-Wallet', icon: QrCode, provider: 'GOPAY, OVO, ShopeePay' },
            { id: 'manual', name: 'Transfer Manual', icon: Banknote, provider: 'BSI (Verify Manual 1x24h)' },
            { id: 'app', name: 'M-Banking Link', icon: Smartphone, provider: 'Langsung dari App' }
        ];

        return (
            <div className="max-w-4xl mx-auto space-y-8 animate-in slide-in-from-bottom duration-500">
                <div className="text-center space-y-2">
                    <h2 className="text-3xl font-black text-foreground tracking-tight">Pilih Metode Pembayaran</h2>
                    <p className="text-sm text-muted-foreground font-medium">Pilih jalur pembayaran yang paling nyaman untuk Anda.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {paymentMethods.map((method) => (
                        <button
                            key={method.id}
                            onClick={() => setPaymentMethod(method.id)}
                            className={`p-6 rounded-[2rem] border-2 transition-all text-left flex items-center gap-6 ${paymentMethod === method.id
                                ? 'bg-primary-soft/50 border-primary shadow-lg'
                                : 'bg-surface border-border-main hover:border-primary/50'
                                }`}
                        >
                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors shadow-inner ${paymentMethod === method.id ? 'bg-primary text-white' : 'bg-background text-muted'
                                }`}>
                                <method.icon className="w-7 h-7" />
                            </div>
                            <div className="grow">
                                <h4 className="text-sm font-black text-foreground tracking-tight">{method.name}</h4>
                                <p className="text-[10px] font-bold text-muted-foreground uppercase mt-0.5 tracking-wider">{method.provider}</p>
                            </div>
                            {paymentMethod === method.id && <CheckCircle2 className="w-6 h-6 text-primary" />}
                        </button>
                    ))}
                </div>

                {paymentMethod && (
                    <div className="pt-8 animate-in slide-in-from-top-4 duration-300">
                        <div className="bg-primary/5 border border-dashed border-primary rounded-[2.5rem] p-10 flex flex-col md:flex-row items-center justify-between gap-8">
                            <div className="space-y-1 text-center md:text-left">
                                <p className="text-[10px] font-black text-primary uppercase tracking-widest">Estimasi Total Pembayaran</p>
                                <h3 className="text-3xl font-black text-primary tabular-nums">
                                    {formatCurrency((selectedInvoice?.amount || 0) + (selectedInvoice?.fine || 0))}
                                </h3>
                            </div>
                            <div className="flex gap-4">
                                <button onClick={handleBack} className="px-8 py-4 bg-white border border-border-main rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-surface">Batal</button>
                                <button
                                    onClick={handleConfirmPayment}
                                    className="px-10 py-4 bg-primary text-white rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.05] transition-all"
                                >
                                    Lanjut Bayar Sekarang
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const SuccessView = () => (
        <div className="max-w-2xl mx-auto text-center space-y-8 animate-in zoom-in-95 duration-700 py-10">
            <div className="w-24 h-24 bg-success-soft rounded-[2.5rem] flex items-center justify-center text-success mx-auto shadow-xl shadow-success/10 relative">
                <CheckCircle2 className="w-12 h-12" />
                <div className="absolute inset-0 bg-success/20 rounded-[2.5rem] animate-ping opacity-20"></div>
            </div>
            <div className="space-y-3">
                <h2 className="text-3xl font-black text-foreground tracking-tight">Pembayaran Anda Diproses!</h2>
                <p className="text-sm text-muted-foreground font-medium max-w-md mx-auto">
                    Terima kasih telah melakukan pembayaran. Jika Anda menggunakan metode otomatis (VA/QRIS), status akan berubah menjadi lunas dalam hitungan menit.
                </p>
            </div>
            <div className="bg-surface border border-border-main border-dashed rounded-[32px] p-8 flex flex-col items-center gap-4">
                <div className="flex items-center gap-4 text-left">
                    <Receipt className="w-8 h-8 text-muted" />
                    <div>
                        <p className="text-[10px] font-black text-muted-foreground uppercase mb-0.5">ID Transaksi</p>
                        <p className="text-sm font-black text-foreground tracking-widest">TRX-77821-BC92</p>
                    </div>
                </div>
            </div>
            <button
                onClick={handleBack}
                className="px-12 py-5 bg-foreground text-background rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-foreground/90 transition-all shadow-xl"
            >
                Kembali ke Dashboard
            </button>
        </div>
    );

    return (
        <main className="max-w-7xl mx-auto w-full p-4 md:p-10 pb-32">
            {viewMode === 'list' && <ListView />}
            {viewMode === 'detail' && <DetailView />}
            {viewMode === 'payment' && <PaymentView />}
            {viewMode === 'success' && <SuccessView />}
        </main>
    );
}
